import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { CloudUpload } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  UploadState,
  FeedbackSnackbar,
  ProgressModal,
  convertTextToSpeech,
  uploadToScreenApp,
  callGeminiAPI,
  useIframeResizer,
} from './common';

// Set worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

interface PDFToAudioState extends UploadState {
  pdfText: string;
  isLoading: boolean;
  loadingMessage: string;
  audioUrl: string | null;
  audioBlob: Blob | null;
  showError: boolean;
  fileName: string;
  podcastSummary: string | null;
}

const createPodcastSummary = async (text: string): Promise<string> => {
  console.log('Creating podcast summary from text:', {
    textLength: text.length,
  });

  const prompt = `
<?xml version="1.0" encoding="UTF-8"?>
<content_transformation_request>
  <instructions>
    <task>Transform this document into clear, well-structured narrative content</task>
    <output_constraints>
      <length>
        <min_words>300</min_words>
        <max_words>2000</max_words>
      </length>
      <structure>
        <sentence_length>15-25 words average</sentence_length>
        <paragraph_length>2-4 sentences</paragraph_length>
      </structure>
    </output_constraints>
    <content_guidelines>
      <style>
        <clarity>Use clear, precise language avoiding complex jargon</clarity>
        <flow>Present information in a logical sequence</flow>
        <tone>Professional and direct</tone>
      </style>
      <structure>
        <introduction>Brief context about the document's main topic</introduction>
        <main_content>
          <organization>Present key points in a clear sequence</organization>
          <emphasis>Focus on essential information and insights</emphasis>
        </main_content>
        <conclusion>
          <summary>Concise recap of main points</summary>
          <takeaway>Key insights or action items</takeaway>
        </conclusion>
      </structure>
      <formatting>
        <numbers>Write numbers in a clear format</numbers>
        <abbreviations>Expand acronyms on first use</abbreviations>
        <lists>Convert lists into flowing narrative</lists>
        <tables>Transform data into descriptive text</tables>
        <visuals>Convert visual information into clear explanations</visuals>
      </formatting>
    </content_guidelines>
  </instructions>
  <source_text>
    ${text}
  </source_text>
</content_transformation_request>`;

  try {
    return await callGeminiAPI(prompt);
  } catch (error) {
    console.error('Error creating podcast summary:', error);
    throw new Error(
      'Failed to create podcast summary. Using original text instead.'
    );
  }
};

const PDFToAudio: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<PDFToAudioState>({
    pdfText: '',
    isLoading: false,
    loadingMessage: '',
    audioUrl: null,
    audioBlob: null,
    showError: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    fileName: '',
    podcastSummary: null,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }
    };
  }, [state.audioUrl]);

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      console.log('Starting PDF text extraction:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
      setState((prev) => ({ ...prev, loadingMessage: 'Reading PDF file...' }));
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      console.log('PDF loaded successfully:', { numPages: pdf.numPages });
      setState((prev) => ({
        ...prev,
        loadingMessage: `Extracting text from ${pdf.numPages} pages...`,
      }));

      const pagePromises = Array.from(
        { length: pdf.numPages },
        async (_, i) => {
          const pageNum = i + 1;
          try {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            setState((prev) => ({
              ...prev,
              loadingMessage: `Processing page ${pageNum} of ${pdf.numPages}...`,
            }));
            return textContent.items
              .filter((item) => 'str' in item && typeof item.str === 'string')
              .map((item) => (item as { str: string }).str)
              .join(' ');
          } catch (error) {
            console.error(`Error extracting text from page ${pageNum}:`, error);
            return '';
          }
        }
      );

      const textContents = await Promise.all(pagePromises);
      await loadingTask.destroy();

      const fullText = textContents.filter(Boolean).join('\n\n').trim();
      if (!fullText) {
        throw new Error(
          'No readable text found in the PDF. The file might be scanned or image-based.'
        );
      }

      console.log('PDF text extraction complete:', {
        textLength: fullText.length,
        firstFewWords: `${fullText.slice(0, 50)}...`,
      });

      return fullText;
    } catch (error) {
      console.error('PDF extraction error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        'Failed to extract text from PDF. Please make sure the file contains readable text.'
      );
    }
  };

  const handleConvert = async (textToConvert?: string) => {
    const text = textToConvert || state.pdfText;
    if (!text.trim()) {
      console.log('No text to convert');
      return;
    }

    console.log('Starting podcast summary generation');
    setState((prev) => ({
      ...prev,
      isLoading: true,
      audioUrl: null,
      audioBlob: null,
      error: null,
      loadingMessage: 'Generating podcast summary...',
    }));

    try {
      const podcastSummary = await createPodcastSummary(text);
      console.log('Podcast summary generated successfully:', {
        originalLength: text.length,
        summaryLength: podcastSummary.length,
      });

      setState((prev) => ({
        ...prev,
        podcastSummary,
        loadingMessage: 'Converting summary to speech...',
      }));

      const blob = await convertTextToSpeech(podcastSummary);
      console.log('Text to speech conversion successful:', {
        blobSize: blob.size,
        blobType: blob.type,
      });

      const url = URL.createObjectURL(blob);
      setState((prev) => ({
        ...prev,
        audioUrl: url,
        audioBlob: blob,
        isLoading: false,
        loadingMessage: '',
      }));

      // Validate blob before upload
      if (!blob || blob.size === 0) {
        throw new Error('Generated audio is empty');
      }

      const audioFileName = 'PDF.mp3';
      console.log('Starting upload to ScreenApp:', {
        audioFileName,
        blobSize: blob.size,
      });

      uploadToScreenApp(
        blob,
        audioFileName,
        () => {
          console.log('Upload started');
          setState((prev) => ({ ...prev, uploading: true, progress: 0 }));
        },
        (progress) => {
          console.log('Upload progress:', { progress });
          setState((prev) => ({ ...prev, progress }));
        },
        () => {
          console.log('Upload completed successfully');
          setState((prev) => ({ ...prev, uploadComplete: true }));
        },
        (error) => {
          console.error('Upload failed:', error);
          setState((prev) => ({
            ...prev,
            error: `Upload failed: ${error}`,
            showError: true,
          }));
        }
      );
    } catch (err) {
      console.error('Processing failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to process text. Please try again.';
      setState((prev) => ({
        ...prev,
        error: errorMessage,
        showError: true,
        isLoading: false,
        loadingMessage: '',
      }));
    }
  };

  const processFile = async (file: File) => {
    console.log('Processing file:', {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    if (!file.type.includes('pdf')) {
      console.error('Invalid file type:', file.type);
      setState((prev) => ({
        ...prev,
        error: 'Please upload a PDF file',
        showError: true,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      fileName: file.name,
      error: null,
      showError: false,
      pdfText: '',
      audioUrl: null,
      audioBlob: null,
      loadingMessage: 'Preparing to read PDF...',
    }));

    try {
      console.log('Starting text extraction');
      const text = await extractTextFromPDF(file);
      console.log('Text extraction successful:', { textLength: text.length });

      setState((prev) => ({
        ...prev,
        pdfText: text,
        isLoading: true,
        loadingMessage: 'Starting audio conversion...',
      }));

      await handleConvert(text);
    } catch (error) {
      console.error('File processing failed:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to read PDF file. Please try again with a different PDF.';

      setState((prev) => ({
        ...prev,
        error: errorMessage,
        showError: true,
        isLoading: false,
        pdfText: '',
        loadingMessage: '',
      }));
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    []
  );

  const handleReset = () => {
    setState({
      pdfText: '',
      isLoading: false,
      loadingMessage: '',
      audioUrl: null,
      audioBlob: null,
      showError: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
      fileName: '',
      podcastSummary: null,
    });
  };

  const handleSaveAgain = () => {
    if (state.audioBlob) {
      uploadToScreenApp(
        state.audioBlob,
        'PDF.mp3',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) =>
          setState((prev) => ({
            ...prev,
            error: `Upload failed: ${error}`,
            showError: true,
          }))
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ p: 3, maxWidth: '60rem', width: '100%' }}>
        <Paper
          elevation={0}
          variant={
            state.isLoading || state.uploading ? 'elevation' : 'outlined'
          }
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: state.isLoading || state.uploading ? 'default' : 'pointer',
            borderStyle: state.isLoading || state.uploading ? 'none' : 'dashed',
            borderWidth: 3,
            borderRadius: '12px',
            bgcolor: isDragging ? 'action.hover' : 'background.paper',
            borderColor: isDragging ? 'primary.main' : 'secondary.dark',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor:
                state.isLoading || state.uploading
                  ? 'transparent'
                  : 'primary.main',
              bgcolor:
                state.isLoading || state.uploading
                  ? 'transparent'
                  : 'action.hover',
              transform:
                state.isLoading || state.uploading
                  ? 'none'
                  : 'translateY(-2px)',
              boxShadow:
                state.isLoading || state.uploading
                  ? 'none'
                  : '0 4px 12px rgba(0,0,0,0.05)',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() =>
            !state.isLoading &&
            !state.uploading &&
            fileInputRef.current?.click()
          }
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept=".pdf"
          />

          {!state.isLoading && !state.uploading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                '@keyframes floatAnimation': {
                  '0%': {
                    transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                  },
                  '50%': {
                    transform: isDragging
                      ? 'scale(1.1) translateY(-5px)'
                      : 'scale(1) translateY(-5px)',
                  },
                  '100%': {
                    transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                  },
                },
              }}
            >
              <CloudUpload
                sx={{
                  fontSize: 56,
                  color: isDragging ? 'primary.main' : 'text.secondary',
                  transition: 'all 0.2s ease-in-out',
                  transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                  '.MuiPaper-root:hover &': {
                    animation: 'floatAnimation 2s ease-in-out infinite',
                  },
                }}
              />

              <Typography variant="h5" fontWeight="medium" sx={{ mb: 1 }}>
                {t('pdfToAudio.controls.upload')}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                or drop your PDF file here
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Supports PDF files only
              </Typography>
            </Box>
          )}

          {state.isLoading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" align="center">
                {state.loadingMessage}
              </Typography>
            </Box>
          )}

          {state.uploading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" align="center">
                Uploading...
              </Typography>
            </Box>
          )}

          {state.error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {state.error}
            </Typography>
          )}
        </Paper>

        {state.audioUrl && (
          <Box sx={{ mt: 3 }}>
            <audio controls src={state.audioUrl} style={{ width: '100%' }}>
              <track kind="captions" />
            </audio>
          </Box>
        )}

        <ProgressModal
          open={state.uploading}
          progress={state.progress}
          isComplete={state.uploadComplete}
          processingMessage={t('pdfToAudio.messages.processing')}
          completeMessage={t('pdfToAudio.messages.complete')}
          onReset={handleReset}
          onSaveAgain={handleSaveAgain}
          onClose={() => setState((prev) => ({ ...prev, uploading: false }))}
        />

        <FeedbackSnackbar
          open={!!state.error && state.showError}
          message={state.error || t('pdfToAudio.messages.error')}
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, showError: false }))}
        />
      </Box>
    </Box>
  );
};

export default PDFToAudio;
